/**
 * TypeScript types pentru sistemul de programări
 */

// Enums
export type AppointmentStatus = 
  | 'scheduled' 
  | 'confirmed' 
  | 'checked_in' 
  | 'in_progress' 
  | 'completed' 
  | 'no_show' 
  | 'cancelled' 
  | 'rescheduled';

export type AppointmentSource = 'manual' | 'his_import' | 'api' | 'portal';
export type AppointmentPriority = 'normal' | 'urgent' | 'emergency';

// Status config pentru UI
export const APPOINTMENT_STATUS_CONFIG: Record<AppointmentStatus, { label: string; color: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' }> = {
  scheduled: { label: 'Programat', color: 'primary' },
  confirmed: { label: 'Confirmat', color: 'info' },
  checked_in: { label: 'Check-in', color: 'warning' },
  in_progress: { label: 'În desfășurare', color: 'secondary' },
  completed: { label: 'Finalizat', color: 'success' },
  no_show: { label: 'Neprezentare', color: 'error' },
  cancelled: { label: 'Anulat', color: 'error' },
  rescheduled: { label: 'Reprogramat', color: 'default' },
};

export const APPOINTMENT_PRIORITY_CONFIG: Record<AppointmentPriority, { label: string; color: 'default' | 'warning' | 'error' }> = {
  normal: { label: 'Normal', color: 'default' },
  urgent: { label: 'Urgent', color: 'warning' },
  emergency: { label: 'Urgență', color: 'error' },
};

// List item
export interface AppointmentListItem {
  id: string;
  
  // Patient
  patient_id: string;
  patient_name: string;
  patient_cnp: string | null;
  patient_phone: string | null;
  
  // Device
  device_id: string;
  device_code: string;
  device_name: string;
  device_room: string;
  
  // Investigation
  investigation_type_id: string;
  investigation_code: string;
  investigation_name: string;
  
  // Scheduling
  scheduled_date: string;
  scheduled_start_time: string;
  scheduled_end_time: string;
  duration_minutes: number;
  
  // Status
  status: AppointmentStatus;
  priority: AppointmentPriority;
  source: AppointmentSource;
  preparation_required: boolean;
  preparation_confirmed: boolean;
  
  created_at: string;
}

// Full read
export interface AppointmentRead {
  id: string;
  tenant_id: string;
  
  patient: {
    id: string;
    cnp: string | null;
    first_name: string;
    last_name: string;
    phone: string | null;
  };
  
  device: {
    id: string;
    code: string;
    name: string;
    room: string;
  };
  
  investigation_type: {
    id: string;
    code: string;
    name: string;
    default_duration_minutes: number;
    preparation_required: boolean;
  };
  
  study_id: string | null;
  
  scheduled_date: string;
  scheduled_start_time: string;
  scheduled_end_time: string;
  duration_minutes: number;
  
  actual_start_time: string | null;
  actual_end_time: string | null;
  
  status: AppointmentStatus;
  priority: AppointmentPriority;
  source: AppointmentSource;
  
  reason: string | null;
  notes: string | null;
  patient_notes: string | null;
  preparation_confirmed: boolean;
  estimated_price: number | null;
  
  external_id: string | null;
  external_system: string | null;
  
  rescheduled_to_id: string | null;
  rescheduled_from_id: string | null;
  
  created_at: string;
  updated_at: string;
  created_by: { id: string; first_name: string; last_name: string } | null;
  cancelled_at: string | null;
  cancelled_by: { id: string; first_name: string; last_name: string } | null;
  cancellation_reason: string | null;
  
  status_history: Array<{
    id: string;
    from_status: AppointmentStatus | null;
    to_status: AppointmentStatus;
    reason: string | null;
    created_at: string;
    changed_by: { id: string; first_name: string; last_name: string } | null;
  }>;
}

// Create
export interface AppointmentCreate {
  patient_id: string;
  device_id: string;
  investigation_type_id: string;
  scheduled_date: string;
  scheduled_start_time: string;
  duration_minutes?: number;
  priority?: AppointmentPriority;
  source?: AppointmentSource;
  reason?: string;
  notes?: string;
  patient_notes?: string;
  estimated_price?: number;
}

// Update
export interface AppointmentUpdate {
  device_id?: string;
  investigation_type_id?: string;
  scheduled_date?: string;
  scheduled_start_time?: string;
  duration_minutes?: number;
  priority?: AppointmentPriority;
  reason?: string;
  notes?: string;
  patient_notes?: string;
  estimated_price?: number;
}

// List response
export interface AppointmentsListResponse {
  items: AppointmentListItem[];
  total: number;
  skip: number;
  limit: number;
}

// Filters
export interface AppointmentsFilters {
  device_id?: string;
  patient_id?: string;
  date_from?: string;
  date_to?: string;
  status?: AppointmentStatus;
  priority?: AppointmentPriority;
  search?: string;
  skip?: number;
  limit?: number;
}

// Time slot
export interface TimeSlot {
  start_time: string;
  end_time: string;
  is_available: boolean;
  appointment_id: string | null;
}

// Availability response
export interface AvailabilityResponse {
  device_id: string;
  date: string;
  duration_minutes: number;
  available_slots: TimeSlot[];
}
